import React, { useState } from "react";

import { channelLabel, formatDate, formatDay } from "../campaignUtils";
import { Action, Alert, Card, EmptyState, HelpTip, Skeleton, StatusPill } from "../shared/StudioControls";
import { ATTENTION, DailyChart, Legend, percent } from "./AutomationCharts";

const total = (rows, statuses) => (rows || [])
  .filter(row => !statuses || statuses.includes(row.status))
  .reduce((sum, row) => sum + Number(row.total || 0), 0);
const clock = value => value ? value.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";

const statusLine = rotation => rotation.ready
  ? `Running · week ${rotation.week}, day ${rotation.week_day} · started ${formatDate(rotation.start_date)}`
  : "Not sending · setup needed";

// The full rotation plan lives behind a "?" so the dashboard stays about results.
const rotationHelp = rotation => [
  "The four-week plan repeats after Week 4.",
  ...(rotation.weeks || rotation.scenarios || []).map((scenario, index) => `Week ${index + 1}: ${scenario.title}`),
  "Every week: Day 1 promotion SMS, Day 3 lifestyle push, Day 5 promotion email, Day 7 playful lifestyle SMS.",
  "Days 2, 4 and 6 are rest days. Day 8 becomes the next week’s Day 1.",
  "Language: Sinhala when the customer prefers it, otherwise English."
];

const Metric = ({ label, value, detail, tone }) => (
  <div className={`cs-auto-metric ${tone ? `cs-auto-metric--${tone}` : ""}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {detail && <small>{detail}</small>}
  </div>
);

const DailyTable = ({ daily }) => (
  <div className="cs-table-wrap" tabIndex="0" role="region" aria-label="Daily sends table">
    <table className="cs-table cs-auto-table">
      <thead><tr><th scope="col">Date</th><th scope="col">Attempted</th><th scope="col">Submitted</th><th scope="col">Needs attention</th></tr></thead>
      <tbody>
        {daily.map(day => (
          <tr key={day.date}>
            <th scope="row">{formatDay(day.date)}</th>
            <td>{day.attempted}</td>
            <td>{day.submitted} <span className="cs-muted">({percent(day.submitted, day.attempted)}%)</span></td>
            <td className={day.failed ? "cs-auto-attention" : ""}>{day.failed ? `! ${day.failed}` : "0"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CampaignWorkspace = ({ analytics, loading, refreshing, retrying, stopping, restarting, error, notice, forbidden, updatedAt, latestOutcomes, outcomeRunId, outcomeError, onRefresh, onOpenCampaign, onRetryFailure, onStopCampaign, onRestartCampaign }) => {
  const [showTable, setShowTable] = useState(false);
  const rotation = analytics && analytics.rotation;
  const runs = analytics ? analytics.generated_content || [] : [];
  const failures = analytics ? analytics.failures || [] : [];
  const daily = analytics ? analytics.daily || [] : [];
  const summary = analytics ? analytics.summary || [] : [];
  const attempted = total(summary);
  const submitted = total(summary, ["submitted"]);
  const attention = total(summary, ATTENTION);
  const activeRuns = runs.filter(run => run.status === "active").length;
  const hasDeliveryData = daily.length > 0;

  return (
    <div className="cs-page cs-page--wide cs-auto-dashboard">
      <header className="cs-page-header">
        <div>
          <h1 className="cs-title">Personalized customer notifications</h1>
          <p className="cs-muted">{rotation ? statusLine(rotation) : "Automatic SMS, push and email personalization"}</p>
        </div>
        <div className="cs-auto-refresh">
          {updatedAt && <span className="cs-muted">Updated {clock(updatedAt)}</span>}
          <Action disabled={refreshing || loading} onClick={onRefresh}>{refreshing ? "Refreshing…" : "Refresh"}</Action>
        </div>
      </header>

      {error && <Alert tone="danger" title="Couldn’t complete that action">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
      {loading && <Skeleton heights={[96, 300, 220]} label="Loading personalized notifications" />}
      {!loading && forbidden && <Alert tone="danger" title="Access required">Active super-admin access is required.</Alert>}
      {!loading && !analytics && !forbidden && (
        <Card>
          <EmptyState title="Couldn’t load personalized notifications" description="Check your connection, then try again.">
            <Action variant="primary" onClick={onRefresh}>Try again</Action>
          </EmptyState>
        </Card>
      )}

      {!loading && analytics && (
        <React.Fragment>
          {!rotation.ready && (
            <Alert tone="warning" title="No messages are being sent">
              {rotation.reason} Automation starts on its own once this is configured on the server.
            </Alert>
          )}

          <section className="cs-auto-overview" aria-label="Notification overview">
            <div className="cs-auto-overview__today">
              <h2 id="cs-auto-today" className="cs-today__title">
                Today’s notifications
                <HelpTip points={rotationHelp(rotation)} label="How notification personalization works" />
              </h2>
              <dl className="cs-today__facts">
                <div><dt>Today</dt><dd>{rotation.channel ? channelLabel(rotation.channel) : "Rest day"}</dd></div>
                <div><dt>Customer need</dt><dd>{rotation.scenario ? rotation.scenario.title : "Not set"}</dd></div>
                <div><dt>Language</dt><dd>English or Sinhala</dd></div>
              </dl>
            </div>
            <div className="cs-auto-overview__metrics">
              <Metric label="Attempted" value={attempted.toLocaleString()} />
              <Metric label="Submitted" value={submitted.toLocaleString()} detail={attempted ? `${percent(submitted, attempted)}%` : null} />
              <Metric label="Attention" value={attention.toLocaleString()} tone={attention ? "bad" : null} />
              <Metric label="Notification sets" value={runs.length} detail={activeRuns ? `${activeRuns} active` : null} />
            </div>
          </section>

          <Card title="Customer activity · latest notification set" description="Activity during the seven days after each customer’s first submitted notification. This does not prove the notification caused the activity."
            aside={outcomeRunId && <Action onClick={() => onOpenCampaign(outcomeRunId, "six-day")}>View results</Action>}>
            {outcomeError && <p className="cs-muted">Outcome data unavailable: {outcomeError}</p>}
            {!outcomeError && !latestOutcomes && <p className="cs-muted">No live run outcomes available yet.</p>}
            {latestOutcomes && <div className="cs-outcome-metrics">
              <Metric label="Messaged customers" value={Number(latestOutcomes.sent_customers || 0).toLocaleString()} />
              <Metric label="Product orders" value={Number((latestOutcomes.medication_orders && latestOutcomes.medication_orders.total) || 0).toLocaleString()} />
              <Metric label="Lab bookings" value={Number((latestOutcomes.lab_bookings && latestOutcomes.lab_bookings.total) || 0).toLocaleString()} />
              <Metric label="Scenario action rate" value={!latestOutcomes.business || !latestOutcomes.business.conversion || latestOutcomes.business.conversion.rate_percent == null ? "Unavailable" : `${latestOutcomes.business.conversion.rate_percent}%`}
                detail={latestOutcomes.business && latestOutcomes.business.conversion && latestOutcomes.business.conversion.provisional ? "Provisional" : null} />
              <Metric label="Completed order value" value={`LKR ${Number((latestOutcomes.business && latestOutcomes.business.completed_order_value) || 0).toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} detail="Not attributed revenue" />
            </div>}
            <p className="cs-muted">Delivered, opened, clicked, costs, net contribution, unsubscribe/complaint rates and control uplift are unavailable without linked event data.</p>
          </Card>

          {hasDeliveryData && (
            <Card
              title="Delivery activity"
              labelledBy="cs-auto-daily"
              description="Last 30 days. Submitted means handed to the provider, not confirmed delivery."
              aside={daily.length > 0 && <Action onClick={() => setShowTable(!showTable)}>{showTable ? "Show chart" : "Show table"}</Action>}
            >
              {showTable ? <DailyTable daily={daily} /> : <React.Fragment><Legend /><DailyChart daily={daily} /></React.Fragment>}
            </Card>
          )}

          {failures.length > 0 && (
            <Card
              title="Needs attention"
              labelledBy="cs-auto-failures"
              help={["Retry continues from the failed step.", "Unknown provider results need a manual check so nobody is messaged twice."]}
            >
              <ul className="cs-failure-list cs-plain-list">
                {failures.map((failure, index) => (
                  <li key={`${failure.campaign_id || "delivery"}-${failure.failed_at}-${index}`} className="cs-failure-item">
                    <div>
                      <strong>{failure.scenario || failure.area}</strong>
                      <StatusPill status={failure.status || "failed"} />
                    </div>
                    <p>{failure.reason || "No reason was recorded."}</p>
                    <small>{[failure.step, failure.location, failure.failed_at && formatDate(failure.failed_at)].filter(Boolean).join(" · ")}</small>
                    <div className="cs-failure-actions">
                      {["retry_generation", "retry_audience"].includes(failure.retry_action) && (
                        <Action variant="primary" disabled={retrying === failure.campaign_id} onClick={() => onRetryFailure(failure)}>
                          {retrying === failure.campaign_id ? "Retrying…" : "Retry"}
                        </Action>
                      )}
              {failure.campaign_id && <Action onClick={() => onOpenCampaign(failure.campaign_id)}>Open notification set</Action>}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card title="Recent notification sets" labelledBy="cs-auto-runs" help={["Each set contains four notification days across one week.", "All customers shows everyone selected. By day shows who receives each send-day notification."]}>
            {runs.length === 0 ? (
              <EmptyState title="No notification sets yet" description="The first set is created automatically on the start date." />
            ) : (
              <ul className="cs-auto-runs cs-plain-list">
                {runs.map(run => {
                  const title = (run.scenario && run.scenario.title) || "Scenario";
                  return (
                    <li key={run.campaign_id}>
                      <div className="cs-auto-runs__main">
                        <strong>{title}</strong>
                        <small className="cs-muted">
                          {run.sample_preview ? "Sample preview · not scheduled" : `From ${formatDay(run.date)} · ${(run.channels || []).map(channelLabel).join(", ") || "Weekly channel plan"}`}
                        </small>
                      </div>
                      {run.sample_preview ? (
                        <div className="cs-auto-runs__actions">
                          <Action onClick={() => onOpenCampaign(run.campaign_id, "six-day")}>Open sample</Action>
                        </div>
                      ) : (
                        <React.Fragment>
                          <StatusPill status={run.status} />
                          <div className="cs-auto-runs__actions">
                            <Action onClick={() => onOpenCampaign(run.campaign_id, "lead-list")} aria-label={`All customers for ${title}`}>All customers</Action>
                            <Action onClick={() => onOpenCampaign(run.campaign_id, "six-day")} aria-label={`Customers by day for ${title}`}>By day</Action>
                            {!["completed", "stopped"].includes(run.status) && (
                              <Action variant="destructive" disabled={stopping === run.campaign_id} onClick={() => onStopCampaign(run)} aria-label={`Stop ${title}`}>
                                {stopping === run.campaign_id ? "Stopping…" : "Stop"}
                              </Action>
                            )}
                            {run.status === "stopped" && (
                              <Action variant="primary" disabled={restarting === run.campaign_id} onClick={() => onRestartCampaign(run)} aria-label={`Restart ${title} from its saved step`}>
                                {restarting === run.campaign_id ? "Restarting…" : "Restart"}
                              </Action>
                            )}
                          </div>
                        </React.Fragment>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </React.Fragment>
      )}
    </div>
  );
};
